import dayjs from "dayjs";
import utc from "dayjs/plugin/utc";
import timezone from "dayjs/plugin/timezone";
import axios from "axios";
import { i18n } from "@/i18n";
const { t } = i18n.global;
import store from "@/store";
import { jwtDecode } from "jwt-decode";
import { hardLogout } from "@/api";

export const UPDATE_ACTION_STORAGE_KEY = "app:update-action";

function broadcastUpdateAction(action, timestamp) {
    localStorage.setItem(
        UPDATE_ACTION_STORAGE_KEY,
        JSON.stringify({
            action,
            timestamp,
            emittedAt: Date.now(),
        })
    );
}

/**
 * Apply an app update action and keep tab-local update marker in sync.
 *
 * @param {"minor-reload"|"major-logout"} action - Action to execute.
 * @param {string} timestamp - Backend update timestamp.
 * @returns {void}
 */
export function applyUpdateAction(action, timestamp) {
    if (timestamp) {
        const user = JSON.parse(sessionStorage.getItem("user") || "{}");
        user.last_seen_update = timestamp;
        sessionStorage.setItem("user", JSON.stringify(user));
    }

    if (action === "minor-reload") {
        window.location.reload();
        return;
    }

    if (action === "major-logout") {
        localStorage.removeItem("accessToken");
        localStorage.removeItem("refreshToken");
        delete axios.defaults.headers.common["Authorization"];
        sessionStorage.clear();
        window.location = "/login?update=1";
    }
}

dayjs.extend(utc);
dayjs.extend(timezone);

/**
 * Formats a UTC datetime string into local time.
 *
 * @param {string} utcTimeString - The UTC timestamp from the backend.
 * @param {string} [format="DD.MM.YYYY - HH:mm"] - Optional format string using dayjs tokens.
 * @returns {string} - The formatted local time string.
 *
 * Example: "2025-02-18T06:22:12.587530Z" -> "18.02.2025 - 08:22" (if user's local time is GMT+2)
 */
export function formatLocalTime(utcTimeString, format = "DD.MM.YYYY - HH:mm") {
    if (!utcTimeString) return "Unknown";

    // Detect the user's time zone from the browser
    const userTimeZone = Intl.DateTimeFormat().resolvedOptions().timeZone;

    // Convert the UTC time to the user's local time zone and format it
    return dayjs.utc(utcTimeString).tz(userTimeZone).format(format);
}

/**
 * Transforms the API response (with snake_case keys)
 * into a structure that matches your form (with camelCase keys).
 * Adjust this function as needed for both truck and ware types.
 */
export function transformEntryData(apiData) {
    return {
        uid: apiData.uid,
        formType: apiData.form_type,
        loadingLocation: apiData.loading_location
            ? {
                  id: apiData.loading_location.id,
                  postalCode: apiData.loading_location.postal_code,
                  dateStart: apiData.loading_location.date_start,
                  dateEnd: apiData.loading_location.date_end,
                  date: apiData.loading_location.date?.map(
                      (dateStr) => new Date(dateStr)
                  ) || [],
                  timeStart: apiData.loading_location.time_start,
                  timeEnd: apiData.loading_location.time_end,
                  country_code: apiData.loading_location.country_code,
                  place_id: apiData.loading_location.place_id,
                  place_display_name: apiData.loading_location.place_display_name,
              }
            : null,
        unloadingLocation: apiData.unloading_location
            ? {
                  id: apiData.unloading_location.id,
                  postalCode: apiData.unloading_location.postal_code,
                  dateStart: apiData.unloading_location.date_start,
                  dateEnd: apiData.unloading_location.date_end,
                  date: apiData.unloading_location.date?.map(
                      (dateStr) => new Date(dateStr)
                  ) || [],
                  timeStart: apiData.unloading_location.time_start,
                  timeEnd: apiData.unloading_location.time_end,
                  country_code: apiData.unloading_location.country_code,
                  place_id: apiData.unloading_location.place_id,
                  place_display_name: apiData.unloading_location.place_display_name,
              }
            : null,
        info: apiData.info
            ? {
                  id: apiData.info.id,
                  weight: apiData.info.weight.toString(),
                  volume: apiData.info.volume,
                  mdp: apiData.info.mdp.toString(),
                  price: apiData.info.price,
                  paymentTerm: apiData.info.payment_term,
                  currency: apiData.info.currency,
                  vatType: apiData.info.vat_type,
              }
            : null,
        transportOptions: apiData.transport_options
            ? apiData.form_type === "truck"
                ? {
                      id: apiData.transport_options.id,
                      truckTransportType:
                          apiData.transport_options.truck_transport_type,
                      truckSpecialFeatures:
                          apiData.transport_options.truck_special_features,
                      truckCoating: apiData.transport_options.truck_coating,
                      truckType: apiData.transport_options.truck_type,
                  }
                : {
                      id: apiData.transport_options.id,
                      truckCoating: apiData.transport_options.truck_coating,
                      platformType: apiData.transport_options.platform_type,
                      escrowAccount: apiData.transport_options.escrow_account,
                      dangerousLoad: apiData.transport_options.dangerous_load,
                      truckSpecialFeatures:
                          apiData.transport_options.truck_special_features,
                      truckTransportType:
                          apiData.transport_options.truck_transport_type,
                  }
            : null,
        notes: apiData.notes,
        created_at: apiData.created_at,
        user: apiData.user,
    };
}

/**
 * Finds the corresponding text for a given enum value.
 * @param {Object} enumObj - The ENUM object to search in.
 * @param {Number} value - The value received from the backend.
 * @returns {String} - The corresponding text value.
 */
export function mapEnumValue(enumObj, value) {
    if (value === null || value === undefined) return "Unknown";

    const numericValue = Number(value);

    if (Array.isArray(enumObj)) {
        const entry = enumObj.find((item) => item.value === numericValue);
        return entry ? entry.text : "Unknown";
    } else {
        const entry = Object.values(enumObj).find(
            (item) => item.value === numericValue
        );
        return entry ? entry.text : "Unknown";
    }
}

export function startUpdateWatcher() {
    const TEN_MIN = 10 * 60 * 1_000;

    async function check() {
        try {
            const res = await axios.get(
                `${process.env.VUE_APP_API_URL}/update-info/`
            );
            const data = Array.isArray(res.data) ? res.data[0] : res.data; // ← handle array

            if (!data || !data.timestamp) return; // nothing to do

            const user = JSON.parse(sessionStorage.getItem("user") || "{}");
            const seen = user.last_seen_update || "";

            if (data.timestamp <= seen) return;

            const ack = async () => {
                await axios.patch(
                    `${process.env.VUE_APP_API_URL}/update-info/ack/`
                );
                user.last_seen_update = data.timestamp;
                sessionStorage.setItem("user", JSON.stringify(user));
            };

            if (data.type === "minor") {
                if (confirm(t("updates.minorPrompt"))) {
                    await ack();
                    broadcastUpdateAction("minor-reload", data.timestamp);
                    applyUpdateAction("minor-reload", data.timestamp);
                }
            } else {
                if (confirm(t("updates.majorPrompt"))) {
                    await ack();
                    broadcastUpdateAction("major-logout", data.timestamp);
                    applyUpdateAction("major-logout", data.timestamp);
                }
            }
        } catch (err) {
            console.error("update check failed", err);
            showAlertModal(store, "Update check failed", "danger", 12000, err);
        }
    }

    check();
    return setInterval(check, TEN_MIN);
}

/**
 * Triggers an alert modal in the application.
 * This function commits a series of mutations to the Vuex store to configure and show an alert modal.
 * @param {Object} context - The Vuex context object, which provides access to store properties and commit mutations.
 * @param {string} message - The message to be displayed in the alert modal.
 * @param {string} type - The type of the alert (e.g., 'success', 'error', 'warning').
 * @param {number} duration - The duration in milliseconds for which the alert should be displayed.
 * @param errorDetails
 */
export function showAlertModal(
    context,
    message,
    type,
    duration,
    errorDetails = ""
) {
    if (type === "danger") {
        // Clear any existing alert and queue
        context.commit("clearCurrentAlert");
        context.dispatch("configureAlert", {
            message,
            type,
            duration,
            visible: true,
            errorDetails,
        });
    } else {
        const currentAlertType = context.state.alert.type;
        if (currentAlertType === "danger" && context.state.alert.show) {
            // Queue the alert if a danger alert is currently being displayed
            context.commit("queueAlert", {
                message,
                type,
                duration,
                errorDetails,
            });
        } else {
            // Show the alert immediately
            context.dispatch("configureAlert", {
                message,
                type,
                duration,
                visible: true,
                errorDetails,
            });
        }
    }
}

const ONE_HOUR = 60 * 60 * 1_000; // 1 hour
const WARN_LEAD = ONE_HOUR;

function msUntilExpiry(token) {
    if (!token) return 0;
    try {
        const { exp } = jwtDecode(token); // exp is in *seconds*
        return Math.max(0, exp * 1000 - Date.now());
    } catch {
        return 0;
    } // malformed → expired
}

export function startSessionWatcher() {
    // single, *real* check function
    const check = async () => {
        const refresh = localStorage.getItem("refreshToken");
        if (!refresh) return; // no token yet / already logged-out

        const remaining = msUntilExpiry(refresh);

        /* ignore the very first tick while the token is still being set */
        if (remaining !== 0 && remaining <= WARN_LEAD) {
            showAlertModal(
                store,
                i18n.global.t("tokenExpired"),
                "warning",
                8000
            );
            hardLogout();
        }
    };

    /* defer first run so login can finish writing the token */
    setTimeout(check, 0); // run once, right after login
    return setInterval(check, ONE_HOUR); // then every hour
}